import React, { useMemo, useState } from 'react';
import {
  Users, CheckCircle, AlertCircle, BookOpen, UserPlus,
  Building2, Search, ChevronLeft, ChevronRight,
} from 'lucide-react';
import type {
  UserReportEntry,
  DepartmentPerformance,
  UserGrowthEntry,
} from '@/services/PagesServices/AnalyticsService';
import { computeBarChart, filterAndPaginate } from '../utils';

interface UsersTabProps {
  userReport: UserReportEntry[];
  deptPerformance: DepartmentPerformance[];
  userGrowth: UserGrowthEntry[];
}

const USERS_PER_PAGE = 10;

const UsersTab: React.FC<UsersTabProps> = ({ userReport, deptPerformance, userGrowth }) => {
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);

  const userSummary = useMemo(() => {
    const total = userReport.length;
    const active = userReport.filter(u => (u.status || '').toLowerCase() === 'active').length;
    const inactive = total - active;
    const withCourses = userReport.filter(u => u.coursesEnrolled > 0).length;
    return { total, active, inactive, withCourses };
  }, [userReport]);

  const userGrowthChartData = useMemo(
    () => computeBarChart(userGrowth.map(d => ({ value: d.newUsers, label: d.month }))),
    [userGrowth]
  );

  const { filtered: filteredUsers, paginated: paginatedUsers, totalPages: totalUserPages } = useMemo(
    () => filterAndPaginate(userReport, userSearch, ['fullName', 'email', 'department', 'role'], userPage, USERS_PER_PAGE),
    [userReport, userSearch, userPage]
  );

  const renderPagination = () => {
    if (totalUserPages <= 1) return null;
    return (
      <div className="analytics-pagination">
        <span className="analytics-pagination-info">
          Showing {(userPage - 1) * USERS_PER_PAGE + 1}–{Math.min(userPage * USERS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length}
        </span>
        <div className="analytics-pagination-controls">
          <button disabled={userPage <= 1} onClick={() => setUserPage(p => p - 1)}><ChevronLeft size={16} /></button>
          {Array.from({ length: Math.min(totalUserPages, 5) }, (_, i) => {
            let page: number;
            if (totalUserPages <= 5) page = i + 1;
            else if (userPage <= 3) page = i + 1;
            else if (userPage >= totalUserPages - 2) page = totalUserPages - 4 + i;
            else page = userPage - 2 + i;
            return (
              <button key={page} className={userPage === page ? 'active' : ''} onClick={() => setUserPage(page)}>{page}</button>
            );
          })}
          <button disabled={userPage >= totalUserPages} onClick={() => setUserPage(p => p + 1)}><ChevronRight size={16} /></button>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* User Summary Cards */}
      <div className="analytics-summary-cards">
        <div className="analytics-summary-card">
          <div className="analytics-summary-card-icon" style={{ background: '#eef2ff', color: '#6366f1' }}><Users size={20} /></div>
          <div className="analytics-summary-card-info">
            <div className="analytics-summary-card-value">{userSummary.total}</div>
            <div className="analytics-summary-card-label">Total Users</div>
          </div>
        </div>
        <div className="analytics-summary-card">
          <div className="analytics-summary-card-icon" style={{ background: '#dcfce7', color: '#16a34a' }}><CheckCircle size={20} /></div>
          <div className="analytics-summary-card-info">
            <div className="analytics-summary-card-value">{userSummary.active}</div>
            <div className="analytics-summary-card-label">Active Users</div>
          </div>
        </div>
        <div className="analytics-summary-card">
          <div className="analytics-summary-card-icon" style={{ background: '#fef2f2', color: '#dc2626' }}><AlertCircle size={20} /></div>
          <div className="analytics-summary-card-info">
            <div className="analytics-summary-card-value">{userSummary.inactive}</div>
            <div className="analytics-summary-card-label">Inactive Users</div>
          </div>
        </div>
        <div className="analytics-summary-card">
          <div className="analytics-summary-card-icon" style={{ background: '#fff3e8', color: '#ff8c42' }}><BookOpen size={20} /></div>
          <div className="analytics-summary-card-info">
            <div className="analytics-summary-card-value">{userSummary.withCourses}</div>
            <div className="analytics-summary-card-label">With Enrollments</div>
          </div>
        </div>
      </div>

      {/* User Growth + Department Distribution */}
      <div className="analytics-grid-2">
        {userGrowthChartData && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <UserPlus size={20} className="analytics-section-icon" />
              User Growth
            </div>
            <div className="analytics-section-subtitle">New user registrations over the past 12 months</div>
            <div className="analytics-chart-container">
              <svg viewBox={`0 0 ${userGrowthChartData.width} ${userGrowthChartData.height}`} className="analytics-svg-chart" preserveAspectRatio="xMidYMid meet">
                {userGrowthChartData.bars.map((bar, i) => (
                  <g key={i}>
                    <rect x={bar.x} y={bar.y} width={bar.width} height={bar.height} rx="4" fill="#6366f1" opacity="0.85" className="analytics-growth-bar">
                      <title>{`${bar.label}: ${bar.value} new users`}</title>
                    </rect>
                    <text x={bar.x + bar.width / 2} y={userGrowthChartData.padding + userGrowthChartData.chartH + 16} textAnchor="middle" fill="var(--color-secondary-text)" fontSize="9">
                      {bar.label.split(' ')[0]}
                    </text>
                    {bar.value > 0 && (
                      <text x={bar.x + bar.width / 2} y={bar.y - 6} textAnchor="middle" fill="var(--color-secondary-text)" fontSize="10" fontWeight="600">
                        {bar.value}
                      </text>
                    )}
                  </g>
                ))}
              </svg>
            </div>
          </div>
        )}

        {deptPerformance.length > 0 && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <Building2 size={20} className="analytics-section-icon" />
              Department Distribution
            </div>
            <div className="analytics-section-subtitle">Users across departments</div>
            <div className="analytics-dept-distribution">
              {deptPerformance.map((dept, i) => {
                const maxUsers = Math.max(...deptPerformance.map(d => d.totalUsers), 1);
                const barPct = (dept.totalUsers / maxUsers) * 100;
                const enrolledPct = dept.totalUsers > 0 ? Math.round((dept.usersWithEnrollments / dept.totalUsers) * 100) : 0;
                return (
                  <div key={i} className="analytics-dept-dist-item">
                    <div className="analytics-dept-dist-label">
                      <span className="analytics-dept-dist-name">{dept.department}</span>
                      <span className="analytics-dept-dist-count">{dept.totalUsers} users</span>
                    </div>
                    <div className="analytics-dept-dist-bar-container">
                      <div className="analytics-dept-dist-bar-bg">
                        <div className="analytics-dept-dist-bar-total" style={{ width: `${barPct}%` }} />
                        <div className="analytics-dept-dist-bar-enrolled" style={{ width: `${barPct * (enrolledPct / 100)}%` }} />
                      </div>
                      <span className="analytics-dept-dist-enrolled-pct">{enrolledPct}% enrolled</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="analytics-trend-legend" style={{ marginTop: 12 }}>
              <div className="analytics-trend-legend-item">
                <div className="analytics-trend-legend-dot" style={{ background: 'var(--color-border)' }} />
                Total Users
              </div>
              <div className="analytics-trend-legend-item">
                <div className="analytics-trend-legend-dot" style={{ background: 'var(--color-primary)' }} />
                Enrolled
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Report Table */}
      {userReport.length > 0 ? (
        <div className="analytics-section">
          <div className="analytics-section-header-row">
            <div>
              <div className="analytics-section-title">
                <Users size={20} className="analytics-section-icon" />
                User Report
              </div>
              <div className="analytics-section-subtitle">{filteredUsers.length} of {userReport.length} users</div>
            </div>
            <div className="analytics-table-search">
              <Search size={16} />
              <input type="text" placeholder="Search users..." value={userSearch} onChange={(e) => { setUserSearch(e.target.value); setUserPage(1); }} />
            </div>
          </div>
          <div className="analytics-table-wrapper">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th>Role</th><th>Department</th>
                  <th>Status</th><th>Enrolled</th><th>Completed</th><th>Certs</th><th>Points</th><th>Last Login</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user, i) => (
                  <tr key={i}>
                    <td>
                      <div className="analytics-user-cell">
                        <div className="analytics-user-avatar">
                          {(user.fullName || '??').split(' ').map(n => n?.[0] || '').join('').substring(0, 2).toUpperCase()}
                        </div>
                        <span style={{ fontWeight: 600 }}>{user.fullName || '\u2014'}</span>
                      </div>
                    </td>
                    <td>{user.email || '\u2014'}</td>
                    <td><span className="analytics-role-badge">{user.role || '\u2014'}</span></td>
                    <td>{user.department || '\u2014'}</td>
                    <td>
                      <span className={`analytics-table-status ${(user.status || '').toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
                        <span className="analytics-status-dot" />
                        {user.status}
                      </span>
                    </td>
                    <td>{user.coursesEnrolled}</td>
                    <td>{user.coursesCompleted}</td>
                    <td>{user.certificatesEarned}</td>
                    <td style={{ fontWeight: 600, color: 'var(--color-primary)' }}>{user.totalPoints.toLocaleString()}</td>
                    <td className="analytics-date-cell">{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : '\u2014'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </div>
      ) : (
        <div className="analytics-section">
          <div className="analytics-empty">
            <Users size={40} />
            <p>No user data available</p>
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(UsersTab);
